import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    TableContainer, 
    Table, 
    TableHead, 
    TableBody, 
    TableRow, 
    TableCell, 
    Paper,
    CircularProgress,
    Alert,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useAuth } from '../components/AuthContext'; 
import { API_BASE_URL } from '../components/api.js';

function DashboardVendas() {
    const [dataBase, setDataBase] = useState(new Date()); 
    const [dashboardData, setDashboardData] = useState([]); 
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // ❗ Mantido conforme solicitado
    const [columnWidths, setColumnWidths] = useState({
        metrica: 100,
        hoje: 100,
        sem1: 100,
        sem4: 100,
        sem5: 100,
        sem52: 100,
        sem53: 100,
        ano: 100
    });

    const { authenticatedFetch, logout } = useAuth();

    const comparisonPeriods = [
        { key: "sem1", label: "Sem-1" },
        { key: "sem4", label: "Sem-4" },
        { key: "sem5", label: "Sem-5" },
        { key: "sem52", label: "Sem-52" },
        { key: "sem53", label: "Sem-53" },
        { key: "ano", label: "Ano" }
    ];

    const getDayOfWeek = (date) => {
        const days = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
        return days[date.getDay()];
    };

    const formatValue = (value, formatType) => {
        if (value === null || value === undefined) return 'N/A';
        
        const floatValue = parseFloat(value);
        if (isNaN(floatValue)) return 'N/A';

        switch (formatType) {
            case 'R$':
                return `R$ ${floatValue.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
            case 'Ratio':
                return (floatValue).toFixed(2).replace('.', ',') + '%';
            case 'Int':
                return Math.round(floatValue).toLocaleString();
            default:
                return floatValue.toLocaleString();
        }
    };

    const fetchData = async (date) => {
        setLoading(true);
        setError(null);

        console.log('===== FRONTEND DEBUG =====');
        console.log('Objeto date recebido:', date);
        console.log('date.getDate():', date.getDate());
        console.log('date.getMonth():', date.getMonth());
        console.log('date.getFullYear():', date.getFullYear());
    
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;

        console.log('formattedDate enviado:', formattedDate);
        
        try {
            const url = `${API_BASE_URL}/api/dashboard-cupons/?date=${formattedDate}`;
            console.log('URL completa:', url);
            const response = await authenticatedFetch(url); 
            
            if (!response.ok) {
                const errorText = await response.text(); 
                
                if (response.status === 401) {
                    logout();
                    throw new Error("Sessão expirada ou não autorizada.");
                }

                throw new Error(`Erro ${response.status}: ${errorText.slice(0, 50)}...`);
            }

            const data = await response.json();
            setDashboardData(data.metrics || []); 

        } catch (err) {
            console.error("Erro ao buscar dados do dashboard:", err.message);
            
            if (err.message.includes("Unexpected token '<'") || err.message.includes("404")) {
                 setError("Falha na API. Verifique se a URL '/api/dashboard-cupons/' está mapeada.");
            } else {
                 setError(`Falha ao carregar dados: ${err.message}`);
            }
           
            setDashboardData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (dataBase instanceof Date && !isNaN(dataBase)) {
            fetchData(dataBase);
        }
    }, [dataBase]);

    const renderDashboardTable = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
                </Box>
            );
        }

        if (error) {
            return <Alert severity="error">{error}</Alert>;
        }

        if (dashboardData.length === 0) {
            return <Alert severity="info">Nenhum dado encontrado para a data selecionada.</Alert>;
        }

        return (
            <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="medium">
                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableRow>
                            <TableCell 
                                rowSpan={2} 
                                sx={{ 
                                    fontWeight: 'bold', 
                                    verticalAlign: 'middle',
                                    width: `${columnWidths.metrica}px`
                                }}
                            >
                                Métrica
                            </TableCell>

                            <TableCell 
                                align="center" 
                                sx={{ 
                                    fontWeight: 'bold',
                                    borderLeft: '1px solid #ddd',
                                    width: `${columnWidths.hoje}px`
                                }}
                            >
                                Data
                            </TableCell>

                            {comparisonPeriods.map(period => (
                                <TableCell
                                    key={period.key}
                                    align="center"
                                    sx={{
                                        fontWeight: 'bold',
                                        borderLeft: '1px solid #ddd',
                                        width: `${columnWidths[period.key]}px`
                                    }}
                                >
                                    {period.label}
                                </TableCell>
                            ))}
                        </TableRow>

                        <TableRow>
                            <TableCell 
                                align="center"
                                sx={{
                                    borderLeft: '1px solid #ddd',
                                    fontSize: '0.875rem',
                                    width: `${columnWidths.hoje}px`
                                }}
                            >
                                {dataBase.toLocaleDateString()}
                                <br/>
                                <Typography variant="caption" color="text.secondary">
                                    {getDayOfWeek(dataBase)}
                                </Typography>
                            </TableCell>

                            {dashboardData[0]?.comparisons.map((comp, idx) => {
                                const periodKey = comparisonPeriods[idx].key;
                                return (
                                    <TableCell 
                                        key={idx}
                                        align="center"
                                        sx={{
                                            borderLeft: '1px solid #ddd',
                                            fontSize: '0.875rem',
                                            width: `${columnWidths[periodKey]}px`
                                        }}
                                    >
                                        {comp.date.split('-').reverse().join('/')}
                                        <br/>
                                        <Typography variant="caption" color="text.secondary">
                                            {comp.day_of_week}
                                        </Typography>
                                    </TableCell>
                                )
                            })}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {dashboardData.map((metric) => (
                            <TableRow key={metric.key}>
                                <TableCell 
                                    sx={{ 
                                        fontWeight: 'bold',
                                        width: `${columnWidths.metrica}px`
                                    }}
                                >
                                    {metric.name}
                                </TableCell>

                                <TableCell 
                                    align="right"
                                    sx={{ 
                                        borderLeft: '1px solid #ddd',
                                        width: `${columnWidths.hoje}px`
                                    }}
                                >
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        {formatValue(metric.base_value, metric.format)}
                                    </Typography>
                                </TableCell>

                                {metric.comparisons.map((comp, idx) => {
                                    const isPositive = comp.diff_perc >= 0;
                                    const diffColor = isPositive ? 'success.dark' : 'error.main'; 
                                    const Icon = isPositive ? ArrowUpwardIcon : ArrowDownwardIcon;

                                    const periodKey = comparisonPeriods[idx].key;

                                    return (
                                        <TableCell 
                                            key={idx} 
                                            align="center"
                                            sx={{ 
                                                borderLeft: '1px solid #eee',
                                                width: `${columnWidths[periodKey]}px`
                                            }}
                                        >
                                            <Typography variant="body2" sx={{ fontWeight: '600' }}>
                                                {formatValue(comp.value, metric.format)}
                                            </Typography>

                                            <Box 
                                                sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'center',
                                                    color: diffColor,
                                                    mt: 0.5 
                                                }}
                                            >
                                                <Icon sx={{ fontSize: 14, mr: 0.5 }} />
                                                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                                    {comp.diff_perc.toFixed(2).replace('.', ',')}%
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return (
        <Box sx={{ flexGrow: 1, padding: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
            
            <Paper elevation={3} sx={{ padding: 2, width: 'auto', alignSelf: 'flex-start' }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                        label="Data Base"
                        value={dataBase}
                        onChange={(newValue) => newValue && setDataBase(newValue)}
                        format="dd/MM/yyyy"
                    />
                </LocalizationProvider>
            </Paper>

            <Paper elevation={3} sx={{ padding: 2, width: 'auto', alignSelf: 'flex-start' }}>
                {renderDashboardTable()}
            </Paper>
        </Box>
    );
}

export default DashboardVendas;
